export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

export function setCookie(
  name: string,
  value: string | object,
  options?: {
    req?: unknown;
    res?: unknown;
    path?: string;
    domain?: string;
    expires?: Date | string;
    maxAge?: number;
    secure?: boolean;
    httpOnly?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
  }
): void {
  if (typeof document === 'undefined') return;

  const cookieValue = typeof value === 'string' ? value : JSON.stringify(value);
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(cookieValue)}`;

  if (options) {
    if (options.path) cookie += `; path=${options.path}`;
    if (options.domain) cookie += `; domain=${options.domain}`;
    if (options.expires) {
      const expires = options.expires instanceof Date ? options.expires : new Date(options.expires);
      cookie += `; expires=${expires.toUTCString()}`;
    }
    if (options.maxAge) cookie += `; max-age=${options.maxAge}`;
    if (options.secure) cookie += '; secure';
    if (options.sameSite) cookie += `; samesite=${options.sameSite}`;
  } else {
    // Default: 1 year expiry
    const expires = new Date();
    expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
    cookie += `; expires=${expires.toUTCString()}; path=/`;
  }

  document.cookie = cookie;
}

const COOKIE_PROBE_NAME = '__hendrxCookieProbe';

/**
 * Whether this browsing context can persist a cookie at all.
 *
 * Probed with the DEFAULT flavour (no `SameSite`, i.e. Lax) deliberately: that is the flavour a
 * cross-site iframe refuses to store, and the one CET's access-management SDK uses for its own
 * bookkeeping. Probing with `SameSite=None; Secure` would succeed in exactly the contexts where that
 * SDK fails, which would defeat the check.
 *
 * Returns false in a cross-site iframe and when the user has blocked cookies — contexts where any
 * cookie-backed session is unreachable, so callers skip cookie-dependent machinery rather than let it
 * retry forever. See `AuthProvider` in the cet authentication module.
 */
export function canPersistCookies(): boolean {
  if (typeof document === 'undefined') return false;

  try {
    setCookie(COOKIE_PROBE_NAME, '1', { path: '/' });
    const persisted = getCookie(COOKIE_PROBE_NAME) === '1';
    // `maxAge: 0` is dropped by setCookie's falsy check, so expire it explicitly.
    setCookie(COOKIE_PROBE_NAME, '', { path: '/', expires: new Date(0) });
    return persisted;
  } catch {
    return false;
  }
}
